import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../../api/client";
import { safeReturnPath } from "../../api/oauth";
import { ApplicationStatus } from "./ApplicationStatus";
import { CvStep } from "./CvStep";
import { DetailsStep } from "./DetailsStep";
import { overallStatus } from "./status";
import { SubmittedStep } from "./SubmittedStep";
import { TokenStep } from "./TokenStep";

// While the candidate works through their agent, the status here follows along.
const STATUS_POLL_MS = 15_000;

export function ApplyPage() {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
    refetchInterval: (query) => {
      const stage = query.state.data?.stage;
      return stage && stage !== "thinking_submitted" && stage !== "withdrawn"
        ? STATUS_POLL_MS
        : false;
    },
  });
  const [detailsDone, setDetailsDone] = useState(false);
  // Set when the candidate came here from an AI app's "Connect" (OAuth) page to add their CV.
  const [params] = useSearchParams();
  const connectPath = safeReturnPath(params.get("connect"));

  if (me.isPending) return <main className="page">Loading…</main>;
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to="/consent" />;
    return (
      <main className="page">
        <p role="alert">We couldn't load your application. Please refresh.</p>
      </main>
    );
  }

  const state = me.data;
  const status = overallStatus(state);
  const step =
    status === "submitted" || status === "withdrawn"
      ? status
      : !state.cv
        ? "cv"
        : state.token.issued || detailsDone
          ? "token"
          : "details";

  return (
    <main className="page">
      <p className="muted">Signed in as {state.github_login ?? state.name}</p>
      <ApplicationStatus state={state} />
      {connectPath && state.cv && status === "in_progress" && (
        <p className="notice" role="status">
          CV saved.{" "}
          <Link className="button" to={connectPath}>
            Continue connecting your AI app
          </Link>
        </p>
      )}
      {step === "submitted" && <SubmittedStep state={state} />}
      {step === "withdrawn" && (
        <section>
          <h1>Application withdrawn</h1>
          <p>This application is closed. Contact us if you think this is a mistake.</p>
        </section>
      )}
      {step === "cv" && <CvStep minWords={state.cv_min_words} />}
      {step === "details" && (
        <DetailsStep initial={state.details} onDone={() => setDetailsDone(true)} />
      )}
      {step === "token" && <TokenStep state={state} />}
    </main>
  );
}
